/* eslint-disable import/no-internal-modules -- reuses the events list's generated input type */
import { gql, useQuery } from '@apollo/client';
import type { EventType } from '../events/graphql/type';

/*
 * One event, by id, through `getEvents` instead of `getEventDetails`: the detail resolver calls the
 * webplayer backend and Simulcasting, and its query also hits the asset service. Every field below
 * comes from the database, so this page loads without touching another service.
 *
 * `event_password`, `event_hidden`, `origin` and `external_url` were added to `AssetWithEvent` for this
 * page — all plain columns — so `graphql_api` deploys first.
 */
const QUERY = gql`
  query GetLiveLiteEvent($input: GetEventsInput!) {
    getEvents(input: $input) {
      result {
        eid
        name
        startTime
        endTime
        eventType
        isLive
        status
        high_priority
        high_priority_note
        location
        location_name
        location_secondary_id
        verifier_email
        event_password
        event_hidden
        origin
        external_url
        checked_user
        operator_name
        receive_iso_recordings
        audio_cue_sheet_url
        client {
          cid
          organizationName
        }
      }
    }
  }
`;

export interface LiveLiteEventDetails {
  eid: string;
  name?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  eventType?: EventType | null;
  isLive?: boolean | null;
  status?: string | null;
  high_priority?: boolean | null;
  high_priority_note?: string | null;
  /** Location id — the lifecycle commands need it. */
  location?: string | null;
  location_name?: string | null;
  location_secondary_id?: string | null;
  verifier_email?: string | null;
  event_password?: string | null;
  event_hidden?: boolean | null;
  origin?: string | null;
  external_url?: string | null;
  /** Already formatted by the resolver as "First Last". */
  checked_user?: string | null;
  operator_name?: string | null;
  receive_iso_recordings?: boolean | null;
  audio_cue_sheet_url?: string | null;
  client?: { cid?: string | null; organizationName?: string | null } | null;
}

interface LiveLiteEventQuery {
  getEvents?: { result: (LiveLiteEventDetails | null)[] } | null;
}

interface LiveLiteEventVariables {
  input: { ids: number[]; ignore_pagination: boolean };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useLiveLiteEvent = (eid: string) => {
  const id = Number(eid);

  const { data, loading, error, refetch } = useQuery<LiveLiteEventQuery, LiveLiteEventVariables>(QUERY, {
    variables: { input: { ids: [id], ignore_pagination: true } },
    /* Same reason as the list: this page's partial events stay out of the full event page's cache. */
    fetchPolicy: 'no-cache',
    skip: !Number.isFinite(id)
  });

  return {
    event: data?.getEvents?.result.find((result): result is LiveLiteEventDetails => !!result?.eid),
    loading,
    error,
    refetch
  };
};
